import { ButtonHTMLAttributes, FC, ReactElement } from 'react';

import { cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/utils/tailwind';

export type ButtonIconProps = {
  variant: 'secondary' | 'teritary' | 'teritary-bright' | 'swipe' | 'floating';
  icon?: ReactElement;
  size: 'xs' | 'sm' | 'md' | 'lg';
  asChild?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-[6px] transition-all disabled:pointer-events-none [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        secondary:
          'bg-custom-warm-grey-25 shadow-button-shadow text-custom-warm-grey-500 outline outline-custom-warm-grey-300 hover:bg-custom-warm-grey-100 focus:outline-custom-warm-grey-400 disabled:bg-custom-warm-grey-100 disabled:outline-custom-warm-grey-200 disabled:text-custom-warm-grey-300',
        teritary:
          'text-custom-warm-grey-400 hover:bg-custom-warm-grey-100 focus:bg-custom-warm-grey-150 disabled:text-custom-warm-grey-300',
        'teritary-bright':
          'text-custom-warm-grey-500 hover:bg-custom-warm-grey-200 focus:bg-custom-warm-grey-300 disabled:text-custom-warm-grey-300',
        swipe:
          '!px-1 !py-2 text-custom-warm-grey-500 bg-custom-warm-grey-50 shadow-sm outline outline-custom-warm-grey-300 hover:bg-custom-warm-grey-100 focus:bg-custom-warm-grey-100 focus:outline-custom-warm-grey-400 disabled:bg-custom-warm-grey-100 disabled:outline-custom-warm-grey-200 disabled:text-custom-warm-grey-300',
        floating:
          'absolute outline outline-custom-warm-grey-400 bg-custom-warm-grey-25 rounded-full shadow-md transitios-colors hover:bg-custom-warm-grey-100 focus:bg-custom-warm-grey-100 focus:outline-custom-warm-grey-500 disabled:opacity-50',
      },
      size: {
        xs: '!p-1',
        sm: '!p-1.5',
        md: '!p-2',
        lg: '!p-2.5',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
    },
  }
);

export const IconButton: FC<ButtonIconProps> = ({
  icon,
  variant,
  size,
  className,
  asChild = false,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp className={cn(buttonVariants({ variant, size, className }))} {...props}>
      {icon}
    </Comp>
  );
};
